/**
 * Profiljusteringspanel för wizard-genererad data.
 *
 * Visas bara när userData.metadata.quality === "modeled" och
 * wizardInput finns sparad. Användaren kan dra i några sliders för
 * att finjustera profilen utan att gå igenom hela wizarden igen.
 *
 * Ändringar debouncas så vi inte regenererar och kallar backend för
 * varje pixel-rörelse av slider.
 */

import { useState, useEffect, useRef } from "react";
import type { UserData } from "../types";
import type { WizardInput, HeatingType } from "../data-sources/wizardGenerator";
import { generateHoursFromWizard } from "../data-sources/wizardGenerator";
import { enrichCsvHours, ApiError } from "../api/client";

interface ProfileTuningProps {
  userData: UserData;
  onUpdated: (data: UserData) => void;
}

const HEATING_OPTIONS: { value: HeatingType; label: string }[] = [
  { value: "apartment", label: "Lägenhet" },
  { value: "district_heating", label: "Villa med fjärrvärme" },
  { value: "heat_pump_ground", label: "Villa med bergvärme" },
  { value: "heat_pump_air", label: "Villa med luftvärmepump" },
  { value: "direct_electric", label: "Villa med direktverkande el" },
];

export default function ProfileTuningPanel({
  userData,
  onUpdated,
}: ProfileTuningProps) {
  const baselineInput = userData.metadata.wizardInput;
  // Stash också i en non-null-form för att tillfredsställa flow-analys efter
  // de useState-anrop som följer. Komponenten renderar inget om input saknas.
  const wizardInput = baselineInput!;

  const [annualKWh, setAnnualKWh] = useState<number>(wizardInput.annualKWh);
  const [heatingType, setHeatingType] = useState<HeatingType>(
    wizardInput.heatingType,
  );
  // Sol och EV kan slås på/av efteråt även om wizarden inte hade dem.
  const [hasSolar, setHasSolar] = useState<boolean>(wizardInput.hasSolar);
  const [solarAnnualKWh, setSolarAnnualKWh] = useState<number>(
    wizardInput.solarAnnualKWh || 8000,
  );
  const [hasEv, setHasEv] = useState<boolean>(wizardInput.hasEv);
  const [evKmPerYear, setEvKmPerYear] = useState<number>(
    wizardInput.evKmPerYear || 15000,
  );
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialRender = useRef<boolean>(true);

  if (!baselineInput) return null;

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      regenerate();
    }, 600);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annualKWh, heatingType, solarAnnualKWh, evKmPerYear, hasSolar, hasEv]);

  async function regenerate() {
    setIsUpdating(true);
    setError(null);
    try {
      const newInput: WizardInput = {
        ...wizardInput,
        annualKWh,
        heatingType,
        hasSolar,
        solarAnnualKWh: hasSolar ? solarAnnualKWh : 0,
        hasEv,
        evKmPerYear: hasEv ? evKmPerYear : 0,
      };
      const generated = generateHoursFromWizard(newInput);
      const enriched = await enrichCsvHours(
        newInput.priceArea,
        generated.hours.map((h) => ({
          from: h.from,
          importKWh: h.importKWh,
          exportKWh: h.exportKWh,
        })),
      );

      const updated: UserData = {
        metadata: {
          ...userData.metadata,
          totalImport: generated.diagnostics.actualAnnualImportKWh,
          totalExport: generated.diagnostics.actualAnnualExportKWh,
          hoursWithMissingPrice: enriched.metadata.hoursWithMissingPrice,
          wizardInput: newInput,
        },
        hours: enriched.hours.map((h) => ({
          from: h.from,
          importKWh: h.importKWh,
          exportKWh: h.exportKWh,
          spotPrice: h.spotPrice,
          ...(h.quarterPrices && { quarterPrices: h.quarterPrices }),
        })),
      };
      onUpdated(updated);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Kunde inte uppdatera profilen. Försök igen.";
      setError(message);
    } finally {
      setIsUpdating(false);
    }
  }

  function reset() {
    setAnnualKWh(wizardInput.annualKWh);
    setHeatingType(wizardInput.heatingType);
    setHasSolar(wizardInput.hasSolar);
    setSolarAnnualKWh(wizardInput.solarAnnualKWh || 8000);
    setHasEv(wizardInput.hasEv);
    setEvKmPerYear(wizardInput.evKmPerYear || 15000);
  }

  return (
    <section className="profile-tuning">
      <header className="profile-tuning-header">
        <h3>Justera profilen</h3>
        {isUpdating && <span className="profile-tuning-status">Räknar om…</span>}
      </header>

      <div className="profile-tuning-field">
        <label htmlFor="tune-annual">
          Total elförbrukning per år
          <strong>{annualKWh.toLocaleString("sv-SE")} kWh</strong>
        </label>
        <input
          id="tune-annual"
          type="range"
          min={1500}
          max={35000}
          step={500}
          value={annualKWh}
          onChange={(e) => setAnnualKWh(parseInt(e.target.value, 10))}
        />
      </div>

      <div className="profile-tuning-field">
        <label htmlFor="tune-heating">Värmekälla</label>
        <select
          id="tune-heating"
          value={heatingType}
          onChange={(e) => setHeatingType(e.target.value as HeatingType)}
        >
          {HEATING_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="profile-tuning-field">
        <label className="profile-tuning-checkbox">
          <input
            type="checkbox"
            checked={hasSolar}
            onChange={(e) => setHasSolar(e.target.checked)}
          />
          <span>Solpaneler</span>
        </label>
        {hasSolar && (
          <div className="profile-tuning-subfield">
            <label htmlFor="tune-solar">
              Solens årsproduktion
              <strong>{solarAnnualKWh.toLocaleString("sv-SE")} kWh</strong>
            </label>
            <input
              id="tune-solar"
              type="range"
              min={500}
              max={25000}
              step={500}
              value={solarAnnualKWh}
              onChange={(e) => setSolarAnnualKWh(parseInt(e.target.value, 10))}
            />
          </div>
        )}
      </div>

      <div className="profile-tuning-field">
        <label className="profile-tuning-checkbox">
          <input
            type="checkbox"
            checked={hasEv}
            onChange={(e) => setHasEv(e.target.checked)}
          />
          <span>Laddar elbil hemma</span>
        </label>
        {hasEv && (
          <div className="profile-tuning-subfield">
            <label htmlFor="tune-ev">
              EV-körning per år
              <strong>{evKmPerYear.toLocaleString("sv-SE")} km</strong>
            </label>
            <input
              id="tune-ev"
              type="range"
              min={1000}
              max={50000}
              step={1000}
              value={evKmPerYear}
              onChange={(e) => setEvKmPerYear(parseInt(e.target.value, 10))}
            />
          </div>
        )}
      </div>

      <button type="button" className="link" onClick={reset}>
        Återställ till ursprunglig profil
      </button>

      {error && <p className="error">{error}</p>}
    </section>
  );
}
